/**
 * VISUAL SYSTEM: THE SINGLE SOURCE OF TRUTH FOR ART & STYLE
 * 
 * This file controls the "Vibe" of the entire application.
 * Changing values here will update everything from buttons to simulation glows.
 */

export const ART_STYLE = {
  NAME: 'PRISTINE_LIGHT', // Options: 'PRISTINE_LIGHT', 'DARK_STUDIO', 'NEON_NIGHT'
  VERSION: '1.0.0',
};

export const COLORS = {
  // Brand Base
  BRAND: {
    PRIMARY: '#09090b',     // Deep Zinc/Black
    SECONDARY: '#f4f4f5',   // Light Zinc
    ACCENT: '#06b6d4',      // Cyan
    DESTRUCTIVE: '#ef4444', 
  },
  
  // UI Surfaces
  SURFACE: {
    BACKGROUND: '#ffffff',
    CARD: '#ffffff',
    POPOVER: '#ffffff',
    BORDER: 'rgba(9, 9, 11, 0.1)',
    INPUT: 'rgba(9, 9, 11, 0.05)',
  },

  // State Colors
  STATE: {
    SUCCESS: '#22c55e',
    WARNING: '#f59e0b',
    ERROR: '#ef4444',
    INFO: '#3b82f6',
  },

  // Special Simulation Glows (Used in rendering)
  GLOWS: {
    CYAN: 'rgba(0, 229, 255, 0.5)',
    PURPLE: 'rgba(213, 0, 249, 0.5)',
    AMBER: 'rgba(255, 234, 0, 0.5)',
    PINK: 'rgba(255, 0, 128, 0.5)',
  }
};

export const TYPOGRAPHY = {
  FAMILIES: {
    SANS: '"Inter", system-ui, sans-serif',
    DISPLAY: '"Space Grotesk", system-ui, sans-serif',
    MONO: '"JetBrains Mono", monospace',
  },
  WEIGHTS: {
    LIGHT: '300',
    REGULAR: '400',
    MEDIUM: '500',
    SEMIBOLD: '600',
    BOLD: '700',
    EXTRABOLD: '800',
    BLACK: '900',
  }
};

export const EFFECTS = {
  SHADOWS: {
    SOFT: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    GLOW: '0 0 20px rgba(6, 182, 212, 0.2)',
    HARDWARE: '0 10px 15px -3px rgb(0 0 0 / 0.05), 0 4px 6px -4px rgb(0 0 0 / 0.05)',
  },
  BLUR: {
    DEFAULT: '16px',
    HEAVY: '40px',
  },
  // Universal Glassmorphism Templates
  GLASS: (color: string = 'rgba(255, 255, 255, 0.8)') => ({
    backgroundColor: color,
    backdropFilter: `blur(${EFFECTS.BLUR.DEFAULT})`,
    border: `1px solid ${COLORS.SURFACE.BORDER}`,
  })
};

export const UI_COMPONENTS = {
  BUTTON: {
    RADIUS: '0.75rem',
    TRANSITION: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    HOVER_SCALE: '0.98',
    ACTIVE_SCALE: '0.95',
  },
  CARD: {
    RADIUS: '1rem',
    PADDING: '1.5rem',
  },
  INPUT: {
    RADIUS: '0.5rem',
  }
};

/**
 * UTILITY: Get CSS Variables for injection into root
 * This allows Tailwind to sync with these TS constants automatically.
 */
export const getSystemCssVariables = () => {
  return {
    '--color-primary': COLORS.BRAND.PRIMARY,
    '--color-background': COLORS.SURFACE.BACKGROUND,
    '--color-border': COLORS.SURFACE.BORDER,
    '--font-sans': TYPOGRAPHY.FAMILIES.SANS,
    '--font-display': TYPOGRAPHY.FAMILIES.DISPLAY,
    '--radius-lg': UI_COMPONENTS.CARD.RADIUS,
  };
};

/** 
 * ART RECIPES: Pre-defined combinations of styles for specific "looks"
 */
export const ART_RECIPES = {
  PRISTINE_LIGHT: {
    bg: 'bg-white',
    text: 'text-zinc-950',
    border: 'border-zinc-950/10',
    glass: 'bg-white/80 backdrop-blur-md border-zinc-950/10',
    btn: 'bg-zinc-950 text-white hover:bg-zinc-800',
  },
  DARK_STUDIO: {
    bg: 'bg-zinc-950',
    text: 'text-zinc-100',
    border: 'border-white/10',
    glass: 'bg-zinc-950/80 backdrop-blur-md border-white/10',
    btn: 'bg-white text-zinc-950 hover:bg-zinc-200',
  },
  NEON_NIGHT: {
    bg: 'bg-black',
    text: 'text-white',
    border: 'border-cyan-500/30',
    glass: 'bg-black/40 backdrop-blur-lg border-cyan-500/20',
    btn: 'bg-cyan-500 text-black hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]',
  }
};
